import { useEffect, useRef, useState } from "react";
import { AlertTriangle, X, Wallet, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Global in-app alert banner that surfaces when the user or the platform
 * is close to / at / over their monthly limits.
 *
 * Two axes:
 *   1) Platform monthly COP budget (only admin+ see this one).
 *   2) User's personal monthly_image_quota (every user with quota > 0).
 *
 * Severity ladders (per axis):
 *   - pct >= 100  → level "over"     — destructive, NOT dismissible (hard stop).
 *   - pct >=  95  → level "critical" — destructive, dismissible.
 *   - pct >=  80  → level "warn"     — amber, dismissible.
 *
 * Dismissal is persisted to localStorage keyed by (axis + threshold-bucket + YYYY-MM)
 * so:
 *   - dismissing "warn" hides it for the rest of the current month, BUT
 *   - crossing into "critical"/"over" re-surfaces a fresh banner automatically,
 *   - and the banner comes back next month regardless.
 *
 * Also fires ONE sonner toast per axis per session when first crossing 80 %,
 * so the user notices even if the banner is somewhere off-screen.
 */
const STORAGE_KEY = "kc_quota_dismissed";
const SESSION_TOASTED = new Set(); // session-scoped

function monthKey() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function readDismissed() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {};
    } catch {
        return {};
    }
}

function writeDismissed(obj) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch {
        /* noop */
    }
}

function levelFor(pct) {
    if (pct >= 100) return "over";
    if (pct >= 95) return "critical";
    if (pct >= 80) return "warn";
    return null;
}

function fmtCop(v) {
    return `$${new Intl.NumberFormat("es-CO", {
        maximumFractionDigits: 0,
    }).format(v || 0)}`;
}

export default function QuotaAlertBanner() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(readDismissed);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!user) {
            setData(null);
            return;
        }
        const load = () => {
            api.get("/credits/consumption")
                .then((r) => {
                    if (mountedRef.current) setData(r.data);
                })
                .catch(() => {});
        };
        load();
        const onUpd = () => load();
        const t = setInterval(load, 30_000);
        window.addEventListener("kc:credits-updated", onUpd);
        return () => {
            clearInterval(t);
            window.removeEventListener("kc:credits-updated", onUpd);
        };
    }, [user]);

    if (!user || !data) return null;

    // --- compute per-axis alerts
    const isAdmin = user.role === "admin" || user.role === "super_admin";

    const alerts = [];

    // (1) Platform budget — only admin+.
    if (isAdmin && data.monthly_budget_cop > 0) {
        const pct = Number(data.percent_used_month || 0);
        const lvl = levelFor(pct);
        if (lvl) {
            alerts.push({
                axis: "budget",
                level: lvl,
                pct,
                icon: Wallet,
                title:
                    lvl === "over"
                        ? "Presupuesto mensual agotado"
                        : lvl === "critical"
                          ? `Presupuesto crítico · ${pct.toFixed(0)} % usado`
                          : `Presupuesto al ${pct.toFixed(0)} %`,
                detail: `Consumo del mes: ${fmtCop(data.cop_month)} de ${fmtCop(
                    data.monthly_budget_cop,
                )} · ${pct.toFixed(0)} % usado. ${
                    lvl === "over"
                        ? "Las próximas generaciones serán bloqueadas hasta ampliar el presupuesto."
                        : "Considera ampliar el presupuesto en Admin."
                }`,
                actionHref: "/admin",
                actionLabel: "Ajustar en Admin",
                testid: `quota-alert-budget-${lvl}`,
            });
        }
    }

    // (2) User personal monthly quota.
    if (data.my_monthly_quota > 0) {
        const pct = (data.my_images_month / data.my_monthly_quota) * 100;
        const lvl = levelFor(pct);
        if (lvl) {
            alerts.push({
                axis: "quota",
                level: lvl,
                pct,
                icon: ImagePlus,
                title:
                    lvl === "over"
                        ? "Cuota mensual de imágenes agotada"
                        : lvl === "critical"
                          ? `Te quedan pocas imágenes · ${pct.toFixed(0)} %`
                          : `Llegaste al ${pct.toFixed(0)} % de tu cuota mensual`,
                detail: `Este mes has generado ${data.my_images_month} de ${
                    data.my_monthly_quota
                } imágenes (${pct.toFixed(0)} %). ${
                    lvl === "over"
                        ? "No podrás generar más hasta que un admin amplíe tu cuota."
                        : "Pide a un admin que amplíe tu cuota si lo necesitas."
                }`,
                testid: `quota-alert-quota-${lvl}`,
            });
        }
    }

    // --- one-shot toast on entering 80%+ band (per axis, per session)
    alerts.forEach((a) => {
        const sig = `${a.axis}:${monthKey()}`;
        if (!SESSION_TOASTED.has(sig)) {
            SESSION_TOASTED.add(sig);
            const fn =
                a.level === "warn"
                    ? toast.warning
                    : a.level === "critical"
                      ? toast.error
                      : toast.error;
            fn(a.title, {
                description: a.detail,
                duration: 6000,
                id: `quota-toast-${a.axis}`,
            });
        }
    });

    // --- filter out dismissed WITHIN the same level; hard "over" is NEVER dismissible.
    const mk = monthKey();
    const visible = alerts.filter((a) => {
        if (a.level === "over") return true;
        const k = `${a.axis}:${a.level}:${mk}`;
        return !dismissed[k];
    });

    if (!visible.length) return null;

    const dismiss = (a) => {
        const k = `${a.axis}:${a.level}:${monthKey()}`;
        const next = { ...dismissed, [k]: 1 };
        setDismissed(next);
        writeDismissed(next);
    };

    return (
        <div
            className="sticky top-0 z-40 flex flex-col"
            data-testid="quota-alert-stack"
        >
            {visible.map((a) => (
                <AlertRow key={a.testid} alert={a} onDismiss={() => dismiss(a)} />
            ))}
        </div>
    );
}

function AlertRow({ alert, onDismiss }) {
    const Icon = alert.icon || AlertTriangle;
    const palette =
        alert.level === "warn"
            ? "bg-amber-400 text-ink-on"
            : "bg-destructive text-ink-on";
    const levelLabel =
        alert.level === "warn"
            ? "80 %"
            : alert.level === "critical"
              ? "95 %"
              : "100 %";

    return (
        <div
            data-testid={alert.testid}
            className={`${palette} px-4 py-2.5 flex items-center gap-3 shadow-glow-sm animate-fade-up`}
        >
            <div className="shrink-0 flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <span className="font-mono text-[9px] uppercase tracking-widest opacity-80 hidden sm:inline">
                    Alerta · {levelLabel}
                </span>
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight truncate">
                    {alert.title}
                </p>
                <p className="text-[11px] opacity-85 leading-tight truncate sm:whitespace-normal">
                    {alert.detail}
                </p>
            </div>
            {alert.actionHref && (
                <a
                    data-testid={`${alert.testid}-action`}
                    href={alert.actionHref}
                    className="shrink-0 rounded-lg bg-ink-950 text-fog-100 px-3 py-1.5 text-xs font-mono uppercase tracking-widest hover:bg-ink-900 transition-colors"
                >
                    {alert.actionLabel}
                </a>
            )}
            {alert.level !== "over" && (
                <button
                    data-testid={`${alert.testid}-dismiss`}
                    onClick={onDismiss}
                    aria-label="Ocultar alerta"
                    className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-ink-on hover:bg-ink-950/10 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}
